import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Handle, Position } from "@xyflow/react";
import { type MouseEvent,useCallback } from "react";

import type { LoopRuleNode, RuleNodeProps } from "../../types/nodes";
import BaseNode from "../BaseNode";
import { useLoopNavigation } from "./loopContext";
import { resolveLoopIterateType } from "./loopTypes";

type LoopNodeProps = RuleNodeProps<LoopRuleNode>;

const LoopNode = (props: LoopNodeProps) => {
	const { id, data } = props;
	const { enterLoop } = useLoopNavigation();

	const tableLabel =
    data.databaseTableName ||
    data.screenTableName ||
    data.collectionName ||
    data.tableName ||
    data.databaseTableVariableName;
	const loopType = resolveLoopIterateType(data);

	const handleEditContents = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			event.stopPropagation();
			enterLoop(id, data.name ?? "Loop", loopType);
		},
		[id, data.name, enterLoop, loopType],
	);

	return (
		<BaseNode {...props}>
			{/* Body content is positioned relative so the "edit loop contents"
			    action can live in the WHITE body area (top-right), not in the
			    colored header where it collided with the node type icon. */}
			<Box sx={{ position: "relative" }}>
			<Tooltip title="Edit loop contents" arrow placement="top">
				<IconButton
					size="small"
					aria-label="Edit loop contents"
					onClick={handleEditContents}
					sx={{
						position: "absolute",
						top: 0,
						right: 0,
						color: "#01D5AB",
						"&:hover": {
							backgroundColor: "rgba(1, 213, 171, 0.12)",
						},
					}}
				>
					<OpenInNew sx={{ fontSize: 16 }} />
				</IconButton>
			</Tooltip>
			<Stack spacing={1}>
				<Typography variant="body2" color="text.secondary">
          Type:{" "}
					<Typography component="span" variant="body2" color="text.primary" style={{ textTransform: "capitalize" }}>
						{loopType}
					</Typography>
				</Typography>
				{loopType === "table" ? (
					tableLabel ? (
						<Typography variant="body2" color="text.secondary">
              Table:{" "}
							<Typography component="span" variant="body2" color="text.primary">
								{tableLabel}
							</Typography>
						</Typography>
					) : (
						<Typography variant="body2" color="text.disabled">
              No table selected
						</Typography>
					)
				) : null}
				{data.loopNodes && data.loopNodes.length > 0 && (
					<Typography variant="caption" color="text.disabled">
						{data.loopNodes.length} node{data.loopNodes.length !== 1 ? "s" : ""} inside
					</Typography>
				)}
			</Stack>
			</Box>
			<Handle type="target" position={Position.Top} id="in" />
		</BaseNode>
	);
};

export default LoopNode;
